import { objectById, questConditions, questRewards } from './schema.js';
import type { Economy } from './Economy.js';
import type { GameConfig, GameObject, Milestone, Placement, Quest } from '../types.js';

export interface QuestStatus {
    quest: Quest;
    satisfied: boolean;
    claimed: boolean;
}

export interface ProgressionSnapshot {
    level: number;
    firedMilestones: string[];
    claimedQuests: string[];
    unlockedObjects?: string[];
}

export type SlotSnapshot = Record<string, number>;

function milestoneMet(
    milestone: Milestone,
    economy: Economy,
    placements: Placement[],
): boolean {
    const trigger = milestone.trigger;

    if (trigger?.type === 'resource-collection') {
        if (!trigger.resource) return false;
        return economy.get(trigger.resource) >= (trigger.quantity ?? 0);
    }

    if (trigger?.type === 'area-expansion') {
        const relevant = trigger.object
            ? placements.filter((p) => p.objectId === trigger.object)
            : placements;
        const occupied = relevant.reduce((sum, p) => sum + p.footprint.w * p.footprint.h, 0);
        return occupied >= (trigger.quantity ?? 0);
    }

    if (trigger?.type === 'new-buildings-unlock') {
        if (!trigger.object) return false;
        const wanted = trigger.level ?? 1;
        return placements.some((placement) =>
            placement.objectId === trigger.object && placement.state.level >= wanted);
    }

    return false;
}

function questSatisfied(quest: Quest, placements: Placement[], economy: Economy): boolean {
    const conditions = questConditions(quest);
    if (!conditions.length) return false;

    return conditions.every((condition) => {
        const needed = condition.quantity ?? 1;

        if (condition.type === 'building') {
            if (!condition.object) return false;
            const built = placements.filter((p) => p.objectId === condition.object).length;
            return built >= needed;
        }

        if (condition.type === 'resource') {
            if (!condition.resource) return false;
            return economy.get(condition.resource) >= needed;
        }

        return false;
    });
}

export class Progression {
    level: number;
    private readonly config: GameConfig;
    private firedMilestones = new Set<string>();
    private claimedQuests = new Set<string>();
    private readonly authoredUnlocked = new Set<string>();
    private readonly unlockedObjects = new Set<string>();
    private readonly authoredSlots = new Map<string, number>();
    private readonly slots = new Map<string, number>();

    constructor(config: GameConfig) {
        this.config = config;
        this.level = config.ui?.level?.value ?? 1;

        (config.objects ?? []).forEach((object) => {
            if (object.locked !== true) {
                this.authoredUnlocked.add(object.id);
                this.unlockedObjects.add(object.id);
            }
        });
        (config.categories ?? []).forEach((category) => {
            if (category.slots != null) {
                this.authoredSlots.set(category.id, category.slots);
                this.slots.set(category.id, category.slots);
            }
        });
    }

    private get milestones(): Milestone[] {
        return this.config.milestones ?? [];
    }

    private get quests(): Quest[] {
        return this.config.quests ?? [];
    }

    isUnlocked(objectId: string): boolean {
        return this.unlockedObjects.has(objectId);
    }

    slotLimit(categoryId: string | undefined): number | null {
        if (!categoryId) return null;
        return this.slots.get(categoryId) ?? null;
    }

    slotSnapshot(): SlotSnapshot {
        return Object.fromEntries(this.slots);
    }

    applyMilestoneRewards(milestone: Milestone): GameObject[] {
        if (!(milestone.rewards ?? []).includes('new-buildings-unlock')) return [];

        return (this.config.objects ?? []).flatMap((object) => {
            if (object.locked !== true || this.unlockedObjects.has(object.id)) return [];
            this.unlockedObjects.add(object.id);
            return [object];
        });
    }

    collectDue(economy: Economy, placements: Placement[] = []): Milestone[] {
        const due: Milestone[] = [];

        this.milestones.forEach((milestone) => {
            if (this.firedMilestones.has(milestone.id)) return;
            if (!milestoneMet(milestone, economy, placements)) return;

            this.firedMilestones.add(milestone.id);
            this.level += 1;
            due.push(milestone);
        });

        return due;
    }

    questStatus(placements: Placement[], economy: Economy): QuestStatus[] {
        return this.quests.map((quest) => ({
            quest,
            satisfied: questSatisfied(quest, placements, economy),
            claimed: this.claimedQuests.has(quest.id),
        }));
    }

    openCount(placements: Placement[], economy: Economy): number {
        return this.questStatus(placements, economy).filter(({ claimed }) => !claimed).length;
    }

    claim(quest: Quest, economy: Economy, placements: Placement[]): boolean {
        if (this.claimedQuests.has(quest.id)) return false;
        if (!questSatisfied(quest, placements, economy)) return false;

        this.claimedQuests.add(quest.id);

        questRewards(quest).forEach((reward) => {
            if (reward.type === 'resource') {
                economy.add(reward.resource, reward.quantity ?? 0);
                return;
            }

            if (reward.type === 'unlock') {
                if (objectById(this.config, reward.object)) this.unlockedObjects.add(reward.object);
                return;
            }

            if (reward.type === 'slot') {
                const current = this.slotLimit(reward.category);
                if (current != null) this.slots.set(reward.category, current + (reward.quantity ?? 1));
            }
        });

        return true;
    }

    snapshot(): ProgressionSnapshot {
        return {
            level: this.level,
            firedMilestones: [...this.firedMilestones],
            claimedQuests: [...this.claimedQuests],
            unlockedObjects: [...this.unlockedObjects],
        };
    }

    restoreSlots(saved: SlotSnapshot | null | undefined, reconstructLegacy = false): void {
        if (saved === undefined && reconstructLegacy) {
            this.slots.clear();
            this.authoredSlots.forEach((value, id) => this.slots.set(id, value));
            this.claimedQuests.forEach((id) => {
                const quest = this.quests.find((candidate) => candidate.id === id);
                if (!quest) return;
                questRewards(quest).forEach((reward) => {
                    if (reward.type !== 'slot') return;
                    const current = this.slotLimit(reward.category);
                    if (current != null) {
                        this.slots.set(reward.category, current + (reward.quantity ?? 1));
                    }
                });
            });
            return;
        }

        Object.entries(saved ?? {}).forEach(([id, value]) => {
            const current = this.slotLimit(id);
            if (current != null && Number.isFinite(value) && value > current) this.slots.set(id, value);
        });
    }

    restore(saved: ProgressionSnapshot | null): void {
        if (!saved) return;
        this.level = saved.level ?? this.level;
        this.firedMilestones = new Set(saved.firedMilestones ?? []);
        this.claimedQuests = new Set(saved.claimedQuests ?? []);
        this.unlockedObjects.clear();
        this.authoredUnlocked.forEach((id) => this.unlockedObjects.add(id));

        if (Array.isArray(saved.unlockedObjects)) {
            saved.unlockedObjects.forEach((id) => {
                if (objectById(this.config, id)) this.unlockedObjects.add(id);
            });
            return;
        }

        // Reconstruct unlock rewards from saves created before unlocks were stored.
        this.firedMilestones.forEach((id) => {
            const milestone = this.milestones.find((candidate) => candidate.id === id);
            if (milestone) this.applyMilestoneRewards(milestone);
        });
        this.claimedQuests.forEach((id) => {
            const quest = this.quests.find((candidate) => candidate.id === id);
            if (!quest) return;
            questRewards(quest).forEach((reward) => {
                if (reward.type === 'unlock' && objectById(this.config, reward.object)) {
                    this.unlockedObjects.add(reward.object);
                }
            });
        });
    }
}
